import { useEffect } from 'react';
import { Button, makeStyles, ProgressBar, Subtitle1, tokens } from '@fluentui/react-components';

import { StatusLabel } from '@shared/components/StatusLabel';
import ShortcutsLayout from '@shared/components/nav/ShortcutsLayout';
import DashboardFilterBar from '../components/DashboardFilterBar';
import DashboardContent from '../components/DashboardContent';
import { getValidDashboardRootIds } from '../helpers/dashboardScope';
import { useDashboardOverviewData } from '../hooks/useDashboardOverviewData';
import { useDashboardSegmentData } from '../hooks/useDashboardSegmentData';
import { useScrollToSegmentWhenReady } from '../hooks/useScrollToSegmentWhenReady';
import { useDashboardUrlState } from '../hooks/useDashboardUrlState';

const useStyles = makeStyles({
  container: {
    position: 'relative',
    width: '100%',
    maxWidth: '1480px',
    margin: '0 auto',
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXL} ${tokens.spacingVerticalXXXL}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
  },
  refreshIndicator: {
    position: 'absolute',
    top: 0,
    right: tokens.spacingHorizontalXL,
    left: tokens.spacingHorizontalXL,
  },
  errorState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalL,
  },
});

const ItemsDashboard = () => {
  const {
    segmentKey: selectedSegmentKey,
    selectedRootIds,
    page,
    pageSize,
    setSelectedRootIds,
    setSelectedSegmentKey,
    setPage,
    replacePage,
    setPageSize,
  } = useDashboardUrlState();
  const { rootOptions, viewModel, metrics, statusChart, segments, isFetching, isPlaceholderData, error, refetch } =
    useDashboardOverviewData({
      selectedRootIds,
    });
  const segmentData = useDashboardSegmentData({
    selectedRootIds,
    segmentKey: selectedSegmentKey,
    page,
    pageSize,
    enabled: Boolean(viewModel),
  });
  const { itemSegmentsRef, onMetricSegmentChange, onSegmentTabChange } = useScrollToSegmentWhenReady({
    selectedRootIds,
    selectedSegmentKey,
    page,
    pageSize,
    segmentData,
    onSelectedSegmentKeyChange: setSelectedSegmentKey,
  });

  useEffect(() => {
    if (!viewModel || isPlaceholderData || selectedRootIds.length === 0) return;
    const validRootIds = getValidDashboardRootIds(
      selectedRootIds,
      rootOptions.map((option) => option._id)
    );
    if (validRootIds.length !== selectedRootIds.length) setSelectedRootIds(validRootIds);
  }, [isPlaceholderData, selectedRootIds, setSelectedRootIds, rootOptions, viewModel]);

  const classes = useStyles();

  return (
    <ShortcutsLayout>
      <main className={classes.container} aria-busy={isFetching}>
        {viewModel && isFetching ? (
          <ProgressBar className={classes.refreshIndicator} aria-label="Refreshing dashboard data" />
        ) : null}
        <Subtitle1>Dashboard</Subtitle1>

        <DashboardFilterBar
          rootOptions={rootOptions}
          selectedRootIds={selectedRootIds}
          onSelectedRootIdsChange={setSelectedRootIds}
        />

        {!viewModel && error ? (
          <div className={classes.errorState} role="alert">
            <span>Loading the dashboard failed!</span>
            <Button appearance="secondary" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <StatusLabel
            data={viewModel}
            loadingMessage="Loading dashboard..."
            render={(resolvedViewModel) => (
              <DashboardContent
                viewModel={resolvedViewModel}
                metrics={metrics}
                statusChart={statusChart}
                selectedSegmentKey={selectedSegmentKey}
                onMetricSegmentChange={onMetricSegmentChange}
                itemSegmentsProps={{
                  segments,
                  segmentData,
                  sectionRef: itemSegmentsRef,
                  selection: { key: selectedSegmentKey, onChange: onSegmentTabChange },
                  pagination: {
                    page,
                    pageSize,
                    onPageChange: setPage,
                    onReplacePage: replacePage,
                    onPageSizeChange: setPageSize,
                  },
                }}
              />
            )}
          />
        )}
      </main>
    </ShortcutsLayout>
  );
};

export default ItemsDashboard;
